import { GlAlert, GlFormCheckbox, GlTooltipDirective, GlButton } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { parseBoolean } from '@gitlab/frontend-utils';
import TimelineEntryItem from '~/vue_shared/components/notes/timeline_entry_item.vue';
import CommentFieldLayout from '/builds/gitlab/gitlab-ee/app/assets/javascripts/notes/components/comment_field_layout.vue?vue3';
import MarkdownEditor from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/markdown/markdown_editor.vue?vue3';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import { TYPENAME_NOTE, TYPENAME_DISCUSSION } from '~/graphql_shared/constants';
import createWikiPageNoteMutation from '~/wikis/wiki_notes/graphql/create_wiki_page_note.mutation.graphql';
import updateWikiPageMutation from '~/wikis/wiki_notes/graphql/update_wiki_page_note.mutation.graphql';
import { updateDraft, clearDraft, getDraft } from '~/lib/utils/autosave';
import { detectAndConfirmSensitiveTokens } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/secret_detection.js?vue3';
import { COMMENT_FORM } from '~/notes/i18n';
import { __ } from '~/locale';
import HelpIcon from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/help_icon/help_icon.vue?vue3';
import { trackSavedUsingEditor } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/markdown/tracking.js?vue3';
import * as constants from '~/notes/constants';
import { confirmAction } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/confirm_via_gl_modal/confirm_action.js?vue3';
import { createNoteErrorMessages, getAutosaveKey } from '../utils';
import WikiDiscussionSignedOut from '/builds/gitlab/gitlab-ee/app/assets/javascripts/wikis/wiki_notes/components/wiki_discussions_signed_out.vue?vue3';
export default {
    name: 'WikiCommentForm',
    i18n: COMMENT_FORM,
    components: {
        GlAlert,
        GlFormCheckbox,
        WikiDiscussionSignedOut,
        TimelineEntryItem,
        CommentFieldLayout,
        MarkdownEditor,
        GlButton,
        HelpIcon
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    inject: [
        'currentUserData',
        'markdownPreviewPath',
        'noteableType',
        'markdownDocsPath',
        'isContainerArchived'
    ],
    props: {
        noteableId: {
            type: String,
            required: true
        },
        noteId: {
            type: String,
            required: true
        },
        discussionId: {
            type: String,
            required: false,
            default: null
        },
        isReply: {
            type: Boolean,
            required: false,
            default: false
        },
        isEdit: {
            type: Boolean,
            required: false,
            default: false
        },
        canSetInternalNote: {
            type: Boolean,
            required: false,
            default: false
        },
        discussionResolved: {
            type: Boolean,
            required: false,
            default: false
        },
        canResolve: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'cancel',
        'creating-note-done',
        'creating-note-failed',
        'creating-note-start',
        'creating-note-success'
    ],
    data () {
        return {
            errors: [],
            timeoutIds: [],
            note: '',
            noteType: constants.COMMENT,
            noteIsInternal: false,
            isSubmitting: false,
            formFieldProps: {
                id: 'wiki-comment-form',
                name: 'wiki-comment-form',
                'aria-label': __('Wiki comment form'),
                placeholder: __('Write a comment or drag your files here...'),
                'data-testid': 'note-textarea',
                class: 'note-textarea js-gfm-input js-autosize markdown-area'
            },
            noteableData: {
                discussion_locked: false,
                confidential: false,
                issue_email_participants: []
            },
            resolve: false,
            unresolve: this.discussionResolved
        };
    },
    computed: {
        autocompleteDataSources () {
            return gl?.GfmAutoComplete?.dataSources;
        },
        userSignedId () {
            return Boolean(this.currentUserData?.id);
        },
        canCreateNote () {
            return !this.isContainerArchived;
        },
        autosaveKey () {
            if (this.userSignedId) {
                return getAutosaveKey(this.noteableType, this.noteId);
            }
            return '';
        },
        autosaveKeyInternalNote () {
            if (this.userSignedId) {
                // eslint-disable-next-line @gitlab/no-hardcoded-urls -- local storage key, not a URL
                return getAutosaveKey(this.noteableType, `${this.noteId}/InternalNote`);
            }
            return '';
        },
        saveButtonTitle () {
            if (this.isReply) return __('Reply');
            if (this.isEdit) return __('Save comment');
            return __('Comment');
        },
        replyOrEdit () {
            return this.isReply || this.isEdit;
        },
        dynamicClasses () {
            return {
                formParent: {
                    'timeline-content': !this.replyOrEdit
                },
                root: {
                    'gl-mt-6': this.replyOrEdit
                }
            };
        },
        showResolveDiscussionToggle () {
            return this.isReply && this.canResolve;
        },
        saveShouldChangeResolvedState () {
            return this.resolve || this.unresolve;
        },
        newResolvedState () {
            return this.discussionResolved ? !this.unresolve : this.resolve;
        },
        noteHasContent () {
            return Boolean(this.note.trim());
        },
        createNoteInput () {
            return this.isEdit ? {
                id: convertToGraphQLId(TYPENAME_NOTE, this.noteId),
                body: this.note
            } : {
                noteableId: this.noteableId,
                body: this.note,
                discussionId: this.isReply ? this.discussionId : null,
                internal: this.noteIsInternal
            };
        },
        discussionToggleResolveInput () {
            return {
                id: this.discussionId || convertToGraphQLId(TYPENAME_DISCUSSION, 0),
                resolve: this.newResolvedState
            };
        },
        editMutationVariables () {
            return {
                input: {
                    id: convertToGraphQLId(TYPENAME_NOTE, this.noteId),
                    body: this.note
                }
            };
        },
        saveMutationVariables () {
            return {
                shouldCreateNote: this.noteHasContent,
                shouldChangeResolvedState: this.saveShouldChangeResolvedState,
                createNoteInput: this.createNoteInput,
                discussionToggleResolveInput: this.discussionToggleResolveInput
            };
        }
    },
    beforeDestroy () {
        this.timeoutIds.forEach((id)=>{
            clearTimeout(id);
        });
    },
    mounted () {
        this.noteIsInternal = parseBoolean(getDraft(this.autosaveKeyInternalNote));
    },
    methods: {
        async handleCancel () {
            if (!this.note.trim()) {
                return this.$emit('cancel');
            }
            const msg = this.isEdit ? __('Are you sure you want to cancel editing this comment?') : __('Are you sure you want to cancel creating this comment?');
            const confirmed = await confirmAction(msg, {
                primaryBtnText: __('Discard changes'),
                cancelBtnText: this.isEdit ? __('Continue editing') : __('Continue creating')
            });
            if (confirmed) {
                return this.$emit('cancel');
            }
            return null;
        },
        dismissError (index) {
            this.errors.splice(index, 1);
        },
        setError (err) {
            this.errors = err;
        },
        async handleSave () {
            this.errors = [];
            this.isSubmitting = true;
            // save to a block scoped variable so the value is still available in the finally block
            const { noteHasContent } = this;
            const noteBackup = this.note;
            if (noteHasContent) {
                const confirmSubmit = await detectAndConfirmSensitiveTokens({
                    content: this.note
                });
                if (!confirmSubmit) {
                    return;
                }
                this.$emit('creating-note-start', {
                    ...this.createNoteInput,
                    individualNote: this.noteType === constants.DISCUSSION
                });
                trackSavedUsingEditor(this.$refs.markdownEditor?.isContentEditorActive, `${this.noteableType}_${this.noteType}`);
            }
            try {
                const discussion = await this.$apollo.mutate({
                    mutation: this.isEdit ? updateWikiPageMutation : createWikiPageNoteMutation,
                    variables: this.isEdit ? this.editMutationVariables : this.saveMutationVariables
                });
                const response = this.isEdit ? discussion.data.updateNote?.note : discussion.data.createNote?.note?.discussion;
                this.noteIsInternal = false;
                if (noteHasContent) {
                    this.note = '';
                    clearDraft(this.autosaveKey);
                    clearDraft(this.autosaveKeyInternalNote);
                    this.$emit('creating-note-success', response);
                }
            } catch (err) {
                this.setError(createNoteErrorMessages(err));
                if (noteHasContent) {
                    this.$emit('creating-note-failed', err);
                }
                this.note = noteBackup;
                this.timeoutIds.push(setTimeout(()=>{
                    this.$refs.markdownEditor.focus();
                }, 100));
            } finally{
                this.isSubmitting = false;
                if (noteHasContent) {
                    this.$emit('creating-note-done');
                }
            }
        },
        onInput (value) {
            if (!this.isSubmitting) this.note = value;
        },
        disableSubmitButton () {
            return !this.noteHasContent && !this.saveShouldChangeResolvedState || this.isSubmitting;
        },
        setInternalNoteCheckbox () {
            updateDraft(this.autosaveKeyInternalNote, this.noteIsInternal);
        }
    }
};
